import { PaymentActionTypes } from '@tassat/common-definitions';
import { AssetTicker } from '@tassat/fred-extended-definitions';
import React from 'react';

import { getTransactionsSync } from 'Containers/client/forms/helpers/sendFormFn';
import WithdrawalConfirmation from 'Containers/client/forms/withdrawalConfirmation';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAccountWithdrawalRelationshipsByFirmId } from 'Selectors/accountsWithdrawalRelationships';
import { getAddressBookContacts } from 'Selectors/addressBook';
import { getAssetFromTicker } from 'Selectors/assets';
import { getSelectedClientAccount } from 'Selectors/common';
import { getNetAssetValuesAsCurCode } from 'Selectors/netAssetValues';
import { getPricesAsCurCode } from 'Selectors/prices';
import { getWithdrawalInstructionsArray } from 'Selectors/withdrawalInstructions';
import { ActionOverlay } from 'SharedComponents/layouts/overlays';
import WalletCompactDetails from 'SharedComponents/wallets/WalletCompactDetails';

import WithdrawalForm from './withdrawalForm';

import type { IWithdrawalProps } from './index';

export function Withdrawal(props: IWithdrawalProps) {
    const {
        title,
        transactionValues,
        setTransactionValues,
        handleWithdrawal,
        goBack,
        accountPositionBalance
    } = props;

    const currentWallet = useAppSelector(getSelectedClientAccount);
    const addressBookOptions = useAppSelector(getAddressBookContacts);
    const withdrawalInstructions = useAppSelector((state) => getWithdrawalInstructionsArray(state, currentWallet?.FirmId));
    const dxyObject = useAppSelector((state) => getAssetFromTicker(state, AssetTicker.USD));
    const navPriceList = useAppSelector(getNetAssetValuesAsCurCode);
    const realTimePriceList = useAppSelector(getPricesAsCurCode);
    const accountRelationships = useAppSelector((state) => getAccountWithdrawalRelationshipsByFirmId(state, currentWallet.FirmId));
    const tfundObject = useAppSelector((state) => getAssetFromTicker(state, AssetTicker.TFND));
    const [ confirm, setConfirm ] = React.useState<boolean>(false);

    const walletDetailHeader = React.useMemo(() => {
        return (
            <WalletCompactDetails
                accountName={currentWallet.AccountName}
                accountNo={currentWallet.linkedBrokerDealerAccount || ''}
            />
        );
    }, [
        currentWallet.AccountName,
        currentWallet.Account
    ]);

    React.useEffect(() => {
        setTransactionValues({
            ...transactionValues,
            Sender: currentWallet
        });
    }, [currentWallet]);

    const confirmWithdrawal = (values) => {
        setTransactionValues({
            ...values,
            Sender: currentWallet
        });
        setConfirm(true);
    };

    return (
        <ActionOverlay
            admin={false}
            goBack={goBack}
            headerDetailsComponent={walletDetailHeader}
            title={title}
            type={PaymentActionTypes['withdrawal'].type}
        >
            {confirm ? (
                <WithdrawalConfirmation
                    cancelConfirmation={() => {
                        if (transactionValues && transactionValues.WithdrawalAmount && typeof transactionValues.WithdrawalAmount === 'number') {
                            setTransactionValues({
                                ...transactionValues,
                                WithdrawalAmount: `${transactionValues.WithdrawalAmount}`
                            });
                            setConfirm(false);
                        } else {
                            setConfirm(false);
                        }
                    }}
                    currentWallet={currentWallet}
                    dxyObject={dxyObject}
                    handleWithdrawal={handleWithdrawal}
                    withdrawalInstruction={withdrawalInstructions.find((instruction) => (
                        instruction.AccountWithdrawalDataId === transactionValues.WithdrawalInstructions.value
                    ))}
                    withdrawalValues={transactionValues}
                />
            ) : (
                <WithdrawalForm
                    formProps={{
                        addressBookOptions: addressBookOptions,
                        currentWallet: currentWallet,
                        accountPositionBalance: accountPositionBalance,
                        getTransactionsSync: getTransactionsSync,
                        handleWithdrawal: confirmWithdrawal,
                        navPriceList: navPriceList,
                        realTimePriceList: realTimePriceList,
                        dxyObject: dxyObject,
                        tfundObject: tfundObject,
                        withdrawalInstructions: withdrawalInstructions,
                        accountRelationships: accountRelationships
                    }}
                />
            )}
        </ActionOverlay>
    );
}